<template>
  <dl
    v-if="icon"
    class="icon-details-glyphs display-inline-flex flex-items-center"
    style="
      --button-margin-bottom: 0;
      --button-background: transparent;
      --button-hover-background: transparent;
      --button-hover-color: var(--fa-dk-blue);
      --button-active-color: var(--fa-dk-blue);
      --margin-bottom: 0;
      --padding-left: 0;
      gap: var(--spacing-4xs);
    "
  >
    <dt
      v-if="!(isDuotone && isCustomIcon)"
      class="muted sr-only"
      style="--margin-bottom: 0; font-weight: var(--font-weight-normal)"
    >
      {{ glyphLabel }}:
    </dt>
    <dd style="--margin-bottom: 0">
      <button
        type="button"
        class="icon-action-glyph-copy flat compact display-inline-block padding-x-2xs"
        style="--button-color: var(--fa-navy)"
        :aria-label="`${
          isDuotone ? (justCopied === 'primaryglyph' ? 'Copied' : 'Copy Primary Layer') : justCopied === 'primaryglyph' ? 'Copied' : 'Copy Glyph'
        }`"
        data-balloon-pos="up"
        @click="copyPrimaryGlyph"
      >
        <span v-if="!isCustomIcon">
          <i
            :class="[`${translatedStyle} fa-${icon.name} fa-lg fa-fw`]"
            style="--fa-secondary-opacity: 0.1"
          />
        </span>
        <span v-if="isCustomIcon && !isDuotone">
          <font-awesome-icon
            :icon="iconDefinition"
            size="lg"
            fixed-width
            style="--fa-secondary-opacity: 0.1"
          />
        </span>

        <span class="sr-only">Copy Glyph</span>
      </button>
      <button
        v-if="isDuotone"
        type="button"
        class="icon-action-glyph-secondary-copy flat compact display-inline-block padding-x-2xs"
        style="--button-color: var(--fa-navy)"
        :aria-label="justCopied === 'secondaryglyph' ? 'Copied' : 'Copy Secondary Layer'"
        data-balloon-pos="up"
        @click="copySecondaryGlyph"
      >
        <span v-if="!isCustomIcon">
          <i
            :class="[`${translatedStyle} fa-${icon.name} fa-lg fa-fw`]"
            style="--fa-secondary-opacity: 1; --fa-primary-opacity: 0.1"
          />
        </span>

        <!-- TODO: add back the code block below when we want Duotone Custom Icon Glyphs to show -->
        <!-- <span v-if="isCustomIcon">
          <font-awesome-icon
            :icon="iconDefinition"
            size="lg"
            fixed-width
            style="--fa-secondary-opacity: 1; --fa-primary-opacity: 0.1"
          />
        </span> -->

        <span class="sr-only">Copy Secondary Glyph</span>
      </button>
    </dd>
  </dl>
</template>

<script>
  import { EventTrackingMixin } from 'app/mixins'
  import { toIconDefinition } from 'app/views/kits/utils'
  import { getLongCode } from 'app/releases.js'

  const JUST_COPIED_TIMEOUT = 2000

  export default {
    name: 'IconGlyphs',

    mixins: [EventTrackingMixin],

    inject: ['currentVersion'],

    props: {
      icon: {
        type: Object
      },
      isCustomIcon: {
        type: Boolean,
        default: false
      },
      isDuotone: {
        type: Boolean
      },
      isUsingModal: {
        type: Boolean,
        required: true
      },
      styling: {
        type: Object
      }
    },

    data() {
      return {
        justCopied: null
      }
    },

    computed: {
      iconDefinition() {
        return toIconDefinition(this.icon)
      },

      glyphLabel() {
        if (this.isDuotone) {
          return 'Glyphs'
        } else {
          return 'Glyph'
        }
      },

      translatedStyle() {
        if (this.currentVersion.number === 5) {
          if (this.styling.family === 'brands') {
            return 'fab'
          } else {
            return `fa${this.styling.style.charAt(0)}`
          }
        } else {
          return getLongCode(this.styling.family, this.styling.style)
        }
      }
    },

    methods: {
      markCopied(what) {
        this.justCopied = what
        setTimeout(() => {
          this.justCopied = null
        }, JUST_COPIED_TIMEOUT)
      },

      copyPrimaryGlyph() {
        if (this.isDuotone && this.currentVersion.number !== 5) {
          this.$clipboard(`${this.icon.name}#`)
        } else {
          // Unicodes for official icons will probably be expressed as
          // hexadecimal strings. Unicodes for custom icons will probably be
          // expressed as numbers (integers).
          // BEWARE: this has regressed twice, resulting in one or the other
          // kinds of values to not work when pasted into desktop document.
          const codePoint = 'number' === typeof this.icon.unicode ? this.icon.unicode : parseInt(this.icon.unicode, 16)

          this.$clipboard(`${String.fromCodePoint(codePoint)}`)
        }
        this.markCopied('primaryglyph')
        this.$posthog.capture('icon_primary_glyph_copy_click', {
          context: `${this.iconModalContext(this.isUsingModal)}`,
          icon_name: this.icon.name,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })
      },

      copySecondaryGlyph() {
        if (this.currentVersion.number !== 5) {
          this.$clipboard(`${this.icon.name}##`)
        } else {
          this.$clipboard(`${String.fromCodePoint(parseInt(`10${this.icon.unicode}`, 16))}`)
        }
        this.markCopied('secondaryglyph')
        this.$posthog.capture('icon_secondary_glyph_copy_click', {
          context: `${this.iconModalContext(this.isUsingModal)}`,
          icon_name: this.icon.name,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })
      }
    }
  }
</script>
